/**
 * O menu lateral.
 *
 * As classes são as MESMAS do sistema anterior (`lateral`, `nav-grupo`,
 * `nav-item`, `nav-ico`, `nav-txt`) porque a folha de estilo é a mesma,
 * copiada sem uma linha alterada. Trocar nome de classe aqui seria
 * redesenhar o menu por acidente.
 *
 * A única diferença real: era `<button>` com `data-pagina` e um ouvinte
 * de clique; agora é `<NavLink>`. Quem decide o que está ativo passa a
 * ser a URL, e não uma variável — o que faz o botão de voltar do
 * navegador funcionar, e faz cada tela ter endereço próprio para
 * favoritar.
 */

import { Fragment } from 'react'
import { NavLink } from 'react-router-dom'
import { GRUPOS, TELAS } from '../navegacao'
import { usarPendencias } from '../ganchos/usarPendencias'
import { usarSessao } from '../ganchos/usarSessao'

interface Props {
  aberto: boolean
  /** Alterna o trilho expandido — é o toque no logotipo. */
  aoAlternar: () => void
  /** Fecha o trilho ao navegar, que é o certo no celular. */
  aoNavegar?: () => void
}

/* ═══ ESTE APARELHO TEM MOUSE? ═══
 *
 * O trilho se abre ao passar o mouse — no computador. No celular, o
 * toque deixa o `:hover` GRUDADO até alguém tocar noutro lugar: escolher
 * "Eventos" trocava de tela e o menu continuava aberto, cobrindo metade
 * de uma tela que já é pequena. Era preciso caçar um ponto vazio.
 *
 * Lido uma vez e não a cada desenho: a resposta não muda enquanto a
 * página está aberta, e `matchMedia` a cada render seria trabalho à toa.
 *
 * `(hover: hover)` pergunta pelo ponteiro PRINCIPAL do aparelho. Num
 * híbrido (notebook com tela sensível) ele responde "sim", que é o certo:
 * quem tem os dois usa o mouse a maior parte do tempo. */
const COM_MOUSE = typeof window !== 'undefined'
  && window.matchMedia?.('(hover: hover)').matches

export function Trilho({ aberto, aoAlternar, aoNavegar }: Props) {
  // O ponto no item de Avisos. Fica no trilho, e não numa faixa dentro
  // da tela, porque o trilho está sempre à vista: a pendência aparece
  // esteja a pessoa onde estiver, sem tomar espaço de nenhuma tela.
  const pendencias = usarPendencias()
  // O que esta empresa contratou. Telas de recurso não contratado não
  // entram no menu — ver o campo `recurso` em `navegacao.ts`.
  const { recursos } = usarSessao()

  return (
    <>
    {/* ═══ O FUNDO QUE FECHA O MENU ═══

        Só no celular, e só com o menu aberto. Sem ele, quem abriu o menu
        e mudou de ideia não tem como fechá-lo a não ser escolhendo algum
        item — e no telefone o menu aberto cobre a tela.

        `aria-hidden`: para o leitor de tela ele não existe; quem navega
        por teclado fecha com Esc ou pelo próprio botão do logotipo. */}
    {aberto && (
      <div className="lateral-fundo" onClick={aoNavegar} aria-hidden="true" />
    )}
    <aside className={'lateral' + (aberto ? ' aberta' : '')
                      + (COM_MOUSE ? ' com-mouse' : '')}>
      {/* O trilho se abre ao passar o mouse. No celular não existe
          "passar o mouse", então o logotipo vira o botão que abre — sem
          isso, quem usa no telefone só vê ícones sem legenda. */}
      <button className="marca" onClick={aoAlternar}
              aria-label={aberto ? 'Recolher o menu' : 'Expandir o menu'}>
        {/* ═══ A MARCA É UMA IMAGEM SÓ, E ELA SE ABRE COM O TRILHO ═══

            Antes eram duas peças: um ícone desenhado à mão em SVG e o
            nome escrito em duas linhas. Saíram as duas — o logotipo já
            traz o desenho e o nome juntos, e manter uma segunda versão
            do símbolo em código é garantir que um dia os dois deixem de
            combinar.

            O corte não é arbitrário. As três barras coloridas são o "e"
            de "Recognis", e ficam entre 50 e 108 px dos 423 da imagem;
            com o trilho recolhido é essa faixa que aparece, centrada.
            Aberto, a janela cresce e revela a palavra inteira. Como é a
            MESMA imagem deslizando, não há troca de arquivo, nem piscada
            no meio da transição. */}
        <span className="marca-logo" />
      </button>

      <nav>
        {/* Ordem por uso: primeiro o que se OLHA todo dia, depois o que
            se CONFIGURA de vez em quando, e por último a manutenção. */}
        {/* Fragment, e não uma <div> por grupo. O <nav> separa os itens
            com `gap`, e `gap` só age entre FILHOS DIRETOS: com a div
            envolvendo cada grupo, o espaço passou a existir entre os
            três blocos e sumiu entre os itens de dentro. É também o que
            faz `nav > .nav-grupo:first-child` voltar a casar. */}
        {GRUPOS.map((grupo, i) => (
          <Fragment key={grupo.chave}>
            {/* Três casos, e não dois:
                · com rótulo  → cabeçalho de sempre;
                · sem rótulo e PRIMEIRO → nada. Cabeçalho ali gastaria a
                  linha de cima do menu para dizer o óbvio, e uma div
                  vazia deixaria o `margin-top` e um degrau em branco;
                · sem rótulo e no meio → só uma régua. O corte é real —
                  acima o que se abre para agir, abaixo o que se abre
                  para consultar — e nomear os dois blocos seria
                  explicar uma divisão que o espaço já mostra. */}
            {grupo.rotulo
              ? <div className="nav-grupo"><span>{grupo.rotulo}</span></div>
              : i > 0 && <hr className="nav-regua" />}
            {TELAS.filter(t => t.grupo === grupo.chave)
                  /* Tela de recurso não contratado não aparece.
                     Não é a proteção — as rotas respondem 403 por
                     conta própria; é não oferecer o que não existe. */
                  .filter(t => !t.recurso || recursos[t.recurso])
                  .map(tela => (
              <NavLink
                key={tela.rota}
                to={tela.rota}
                onClick={aoNavegar}
                className={({ isActive }) =>
                  'nav-item' + (isActive ? ' ativo' : '')}
              >
                <span className="nav-ico">
                  <svg className="ico" viewBox="0 0 16 16" fill="none"
                       stroke="currentColor" strokeWidth="1.5"
                       strokeLinecap="round" strokeLinejoin="round">
                    <path d={tela.icone} />
                  </svg>
                </span>
                <span className="nav-txt">{tela.nome}</span>
                {/* Recolhido, o selo vira um ponto na quina do ícone;
                    aberto, vira o número ao lado do nome. O CSS já faz
                    as duas coisas — e 'acao' é o que pulsa. */}
                {tela.rota === '/avisos' && pendencias.length > 0 && (
                  <span className="nav-selo acao">{pendencias.length}</span>
                )}
              </NavLink>
            ))}
          </Fragment>
        ))}
      </nav>

      <div className="lateral-rodape">
        <Conta />
        <Pulso />
      </div>
    </aside>
    </>
  )
}

/**
 * Quem está logado, e a saída.
 *
 * ═══ POR QUE O NOME APARECE, E NÃO SÓ O BOTÃO ═══
 *
 * Num posto de portaria o computador é compartilhado e fica ligado o
 * turno inteiro. Sem o nome à vista, quem senta às 14h não tem como
 * saber que continua na sessão de quem saiu ao meio-dia — e tudo o que
 * fizer fica na auditoria com o nome errado.
 *
 * Recolhido sobra a inicial, que é o suficiente para perceber que mudou.
 */
function Conta() {
  const { usuario, sair } = usarSessao()
  if (!usuario) return null

  const PAPEL: Record<string, string> = {
    dono: 'Dona do sistema', gerente: 'Gerente', operador: 'Operador',
  }

  return (
    <div className="conta">
      <span className="conta-inicial" aria-hidden="true">
        {usuario.nome.trim().charAt(0).toUpperCase()}
      </span>
      <span className="conta-quem">
        <strong>{usuario.nome}</strong>
        <span>{PAPEL[usuario.papel] ?? usuario.papel}</span>
      </span>
      {/* `type=button` porque o trilho tem um <button> por fora (a marca,
          que abre o menu no celular) e um clique aqui não pode subir e
          abrir/fechar o menu junto. */}
      <button type="button" className="conta-sair" onClick={sair}
              data-dica={`Sair da conta de ${usuario.nome}`}
              aria-label="Sair">
        <svg viewBox="0 0 20 20" fill="none" stroke="currentColor"
             strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12.5 6V4.2a1.2 1.2 0 00-1.2-1.2H4.7a1.2 1.2 0 00-1.2 1.2v11.6a1.2 1.2 0 001.2 1.2h6.6a1.2 1.2 0 001.2-1.2V14" />
          <path d="M16.8 10H8m8.8 0l-2.6-2.6M16.8 10l-2.6 2.6" />
        </svg>
      </button>
    </div>
  )
}

/**
 * O ponto do rodapé: verde quando a API responde, cinza quando não.
 *
 * Isto deixou de ser detalhe ao separar o front do back. Antes os dois
 * vinham da mesma porta: se a página abriu, a API estava de pé. Agora a
 * página vem do Cloudflare e a API de outra máquina, e "a tela abriu mas
 * está tudo vazio" passou a ser um estado possível — que sem este ponto
 * seria indistinguível de "não há nada para mostrar".
 */
import { useEffect, useState } from 'react'
import { api } from '../api/cliente'

function Pulso() {
  const [ligado, setLigado] = useState<boolean | null>(null)

  useEffect(() => {
    let vivo = true
    const bater = async () => {
      try {
        await api.ler('/api/estado')
        if (vivo) setLigado(true)
      } catch {
        if (vivo) setLigado(false)
      }
    }
    void bater()
    const t = setInterval(() => void bater(), 15000)
    return () => { vivo = false; clearInterval(t) }
  }, [])

  const texto = ligado === null ? 'Conectando…'
    : ligado ? 'Conectado' : 'Servidor fora de alcance'

  // As classes do ponto são 'on' e 'off' — é o que a folha de estilo
  // conhece. Sem nenhuma das duas ele fica cinza, que é o estado certo
  // enquanto a primeira resposta não chegou.
  const marca = ligado === null ? '' : ligado ? ' on' : ' off'
  return (
    <div className="pulso">
      <span className={'ponto' + marca} />
      <span>{texto}</span>
    </div>
  )
}
